export interface AuthSession {
  email: string
  name?: string
  idToken?: string
  accessToken?: string
  refreshToken?: string
  expiresAt?: number
  workspaceId?: string
  role?: string
  createdAt: string
}

const STORAGE_KEY = 'hostingportal.auth.session'
const SESSION_EVENT = 'hostingportal-auth-session'
const LIVE_AUTH_REQUIRES_TOKEN = (import.meta.env.VITE_HOSTINGPORTAL_RUNTIME ?? 'live') !== 'mock'
let memorySession: string | null = null

export const ALLOWED_EMAIL_DOMAINS = [
  'hosting.com',
  'worldhost.group',
  'growthandanalytics.com',
] as const

export function emailDomain(email: string): string {
  return email.trim().toLowerCase().split('@').at(1) ?? ''
}

export function isAllowedEmail(email: string): boolean {
  const domain = emailDomain(email)
  return ALLOWED_EMAIL_DOMAINS.includes(domain as (typeof ALLOWED_EMAIL_DOMAINS)[number])
}

export function allowedDomainMessage(): string {
  return `Use a ${ALLOWED_EMAIL_DOMAINS.map((d) => `@${d}`).join(' or ')} email.`
}

export function readAuthSession(): AuthSession | null {
  if (typeof window === 'undefined') return null
  const raw = readSessionRaw()
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<AuthSession>
    if (!parsed.email || !isAllowedEmail(parsed.email)) return null
    // A refresh token keeps the login persistent well past the ~1h ID-token
    // expiry: `ensureFreshToken()` silently re-mints the ID token before use, and
    // only a failed refresh (revoked token) clears the session. Without a refresh
    // token (e.g. local-dev), fall back to the ID-token expiry.
    const hasRefresh = Boolean(parsed.refreshToken)
    if (!hasRefresh && parsed.expiresAt && parsed.expiresAt <= Date.now()) return null
    if (LIVE_AUTH_REQUIRES_TOKEN && !parsed.idToken && !parsed.accessToken && !hasRefresh) {
      return null
    }
    return {
      email: parsed.email,
      name: parsed.name,
      idToken: parsed.idToken,
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      expiresAt: parsed.expiresAt,
      workspaceId: parsed.workspaceId,
      role: parsed.role,
      createdAt: parsed.createdAt ?? new Date().toISOString(),
    }
  } catch {
    return null
  }
}

export function writeAuthSession(input: {
  email: string
  name?: string
  idToken?: string
  accessToken?: string
  refreshToken?: string
  expiresAt?: number
  workspaceId?: string
  role?: string
}): AuthSession {
  const session: AuthSession = {
    email: input.email.trim().toLowerCase(),
    name: input.name?.trim() || undefined,
    idToken: input.idToken,
    accessToken: input.accessToken,
    refreshToken: input.refreshToken,
    expiresAt: input.expiresAt,
    workspaceId: input.workspaceId,
    role: input.role,
    createdAt: new Date().toISOString(),
  }
  writeSessionRaw(JSON.stringify(session))
  syncAuthGlobals()
  window.dispatchEvent(new Event(SESSION_EVENT))
  return session
}

export function clearAuthSession(): void {
  if (typeof window === 'undefined') return
  writeSessionRaw(null)
  syncAuthGlobals()
  window.dispatchEvent(new Event(SESSION_EVENT))
}

export function isAuthenticated(): boolean {
  return readAuthSession() !== null
}

export function authToken(): string | null {
  const session = readAuthSession()
  return session?.idToken ?? session?.accessToken ?? null
}

// ── Persistent-login token refresh ────────────────────────────────────────────
// The login stores a long-lived refresh token; `ensureFreshToken` swaps it for a
// new ID token before the ~1h ID token expires, so the session survives as long as
// the refresh token is valid (days/weeks, until revoked). The concrete refresher
// is injected by boot (provider-specific) to avoid a module cycle.

type TokenRefresher = (session: AuthSession) => Promise<AuthSession | null>
let tokenRefresher: TokenRefresher | null = null
let refreshInFlight: Promise<AuthSession | null> | null = null
const REFRESH_SKEW_MS = 5 * 60 * 1000

export function setTokenRefresher(fn: TokenRefresher | null): void {
  tokenRefresher = fn
}

/**
 * Return a non-expired bearer token, silently refreshing via the refresh token
 * when the current ID token is missing or within REFRESH_SKEW_MS of expiry.
 * Concurrent callers share one in-flight refresh. A definitive refresh failure
 * clears the session; a transient error keeps it (returns the stale token).
 */
export async function ensureFreshToken(): Promise<string | null> {
  const session = readAuthSession()
  if (!session) return null
  const expiresSoon = session.expiresAt ? session.expiresAt - Date.now() <= REFRESH_SKEW_MS : false
  if (session.idToken && !expiresSoon) return session.idToken
  if (!session.refreshToken || !tokenRefresher) {
    return session.idToken ?? session.accessToken ?? null
  }
  if (!refreshInFlight) {
    refreshInFlight = Promise.resolve(tokenRefresher(session))
      .then((next) => {
        if (next) {
          writeAuthSession(next)
          return next
        }
        clearAuthSession() // refresh token revoked/expired → sign out
        return null
      })
      .catch(() => null) // transient error → keep the session, retry later
      .finally(() => {
        refreshInFlight = null
      })
  }
  const refreshed = await refreshInFlight
  return refreshed?.idToken ?? refreshed?.accessToken ?? session.idToken ?? null
}

export function authWorkspaceId(): string | null {
  return readAuthSession()?.workspaceId ?? null
}

export function syncAuthGlobals(): void {
  const workspaceId = authWorkspaceId()
  const global = globalThis as { __HOSTINGPORTAL_WORKSPACE_ID__?: string }
  if (workspaceId) global.__HOSTINGPORTAL_WORKSPACE_ID__ = workspaceId
  else global.__HOSTINGPORTAL_WORKSPACE_ID__ = undefined
}

export function installAuthenticatedFetch(): void {
  if (typeof window === 'undefined') return
  const global = globalThis as {
    fetch: typeof fetch
    __HOSTINGPORTAL_AUTH_FETCH_INSTALLED__?: boolean
  }
  if (global.__HOSTINGPORTAL_AUTH_FETCH_INSTALLED__) return
  const originalFetch = global.fetch.bind(global)
  global.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const requestUrl =
      typeof input === 'string' || input instanceof URL
        ? input.toString()
        : input instanceof Request
          ? input.url
          : ''
    const url = new URL(requestUrl, window.location.origin)
    const shouldAttach =
      url.origin === window.location.origin &&
      (url.pathname.startsWith('/api/') ||
        url.pathname === '/graphql' ||
        url.pathname.startsWith('/mcp/'))
    if (!shouldAttach) return originalFetch(input, init)
    const token = await ensureFreshToken()
    if (!token) return originalFetch(input, init)
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : {}))
    headers.set('authorization', `Bearer ${token}`)
    // Active brand (workspace) - lets the backend scope reads to the selected brand
    // so the brand switcher shows that brand's data (local-dev brand-switch path).
    const ws = authWorkspaceId()
    if (ws) headers.set('x-hostingportal-workspace', ws)
    return originalFetch(input, { ...init, headers })
  }
  global.__HOSTINGPORTAL_AUTH_FETCH_INSTALLED__ = true
}

export function authSessionSnapshot(): string {
  if (typeof window === 'undefined') return ''
  return readSessionRaw() ?? ''
}

export function subscribeAuthSession(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener('storage', cb)
  window.addEventListener(SESSION_EVENT, cb)
  return () => {
    window.removeEventListener('storage', cb)
    window.removeEventListener(SESSION_EVENT, cb)
  }
}

function readSessionRaw(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage?.getItem(STORAGE_KEY) ?? memorySession
  } catch {
    return memorySession
  }
}

function writeSessionRaw(value: string | null): void {
  memorySession = value
  if (typeof window === 'undefined') return
  try {
    if (value === null) window.localStorage?.removeItem(STORAGE_KEY)
    else window.localStorage?.setItem(STORAGE_KEY, value)
  } catch {
    // Some embedded or hardened browsers disable Web Storage. The in-memory
    // session keeps the single-page app usable for the current tab.
  }
}
